import { useEffect, useMemo, useState } from 'react';
import * as M from '@mantine/core';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useRowSelection } from '../../../../../shared/hook/useRowSelection';
import type { IModem, IModemFilterForm } from '../../../../../shared/types/modem';
import { cleanParams } from '../../../../../shared/utils/cleanParams';
import * as C from '../../../../ui/common/CommonStyles';
import ModemFilterSection from './ui/filterSection';
import ModemTable from './ui/modemTable';
import ModemRegister from './ui/modemRegister';
import useGetModems from '../../../../../shared/hook/useGetModems';
import useDeleteModems from './hooks/useDeleteModems';
import { showSuccessNotification } from '../../../../ui/notification';
import { queryClient } from '../../../../../shared/queryClient';
import { queries } from '../../../../../shared/queries';

const parseActivationStatuses = (value: string | null): string[] => {
  if (!value) {
    return [];
  }

  return value
    .split(',')
    .map((status) => status.trim())
    .filter(Boolean);
};

const initialFilterForm: IModemFilterForm = {
  startDate: null,
  endDate: null,
  expiredContractOnly: false,
  activationStatuses: [],
  keyword: '',
};

export default function ModemListContainer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterForm, setFilterForm] = useState<IModemFilterForm>(initialFilterForm);

  const pg = searchParams.get('pg') || '1';
  const pgSize = searchParams.get('pgSize') || '10';
  const startDate = searchParams.get('activateStartDtFrom') || '';
  const endDate = searchParams.get('activateStartDtTo') || '';
  const expiredContractOnly = searchParams.get('expiredContractOnly') || '';
  const activationStatusesParam = searchParams.get('activateStatuses');
  const activationStatuses = useMemo(() => parseActivationStatuses(activationStatusesParam), [activationStatusesParam]);
  const keyword = searchParams.get('keyword') || '';

  const { data: modemList } = useGetModems({
    pg: Number(pg),
    pgSize: Number(pgSize),
    activateStartDtFrom: startDate,
    activateStartDtTo: endDate,
    expiredContractOnly,
    activateStatuses: activationStatusesParam || '',
    keyword,
    listType: 'ALL',
  });

  const {
    selectedIds: checkBoxArr,
    isAllSelected,
    toggleItem,
    toggleAll,
    resetSelection: resetCheckBoxArr,
  } = useRowSelection<IModem, number>({
    items: modemList?.list || [],
    getId: (item) => item.id,
    isSelectable: (item) => item.activateStatusYn !== 'Y',
  });

  const { mutate: deleteModems } = useDeleteModems();

  const updateFilterForm = (nextFilter: Partial<IModemFilterForm>) => {
    setFilterForm((prev) => {
      const mergedFilter = {
        ...prev,
        ...nextFilter,
      };

      if (
        mergedFilter.startDate &&
        mergedFilter.endDate &&
        dayjs(mergedFilter.endDate).isBefore(dayjs(mergedFilter.startDate), 'day')
      ) {
        void Swal.fire({
          icon: 'error',
          title: '날짜 선택 오류',
          text: '종료일은 시작일보다 빠를 수 없습니다.',
          confirmButtonText: '확인',
        });

        return prev;
      }

      return mergedFilter;
    });
  };

  const applyFilters = (nextFilter: IModemFilterForm) => {
    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        pg: '1',
        activateStartDtFrom: nextFilter.startDate
          ? dayjs(nextFilter.startDate).startOf('day').toISOString().substring(0, 19)
          : '',
        activateStartDtTo: nextFilter.endDate ? dayjs(nextFilter.endDate).endOf('day').toISOString().substring(0, 19) : '',
        expiredContractOnly: nextFilter.expiredContractOnly ? 'true' : '',
        activateStatuses: nextFilter.activationStatuses.join(','),
        keyword: nextFilter.keyword,
      }),
    );
  };

  const resetFilters = () => {
    setFilterForm(initialFilterForm);

    const currentParams = Object.fromEntries(searchParams.entries());
    setSearchParams(
      cleanParams({
        ...currentParams,
        pg: '1',
        activateStartDtFrom: '',
        activateStartDtTo: '',
        expiredContractOnly: '',
        activateStatuses: '',
        keyword: '',
      }),
    );
  };

  const onClickDelete = () => {
    if (checkBoxArr.length === 0) {
      Swal.fire({
        title: '삭제할 모뎀을 선택해주세요.',
        icon: 'warning',
        confirmButtonText: '확인',
      });
      return;
    }

    Swal.fire({
      title: '모뎀을 삭제하시겠습니까?',
      text: '사용 중인 모뎀은 선택할 수 없습니다.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '확인',
      cancelButtonText: '취소',
    }).then((result) => {
      if (result.isConfirmed) {
        deleteModems(
          { ids: checkBoxArr },
          {
            onSuccess: () => {
              resetCheckBoxArr();
              showSuccessNotification('삭제 되었습니다.');
              queryClient.invalidateQueries(queries.modems.modemList._def);
            },
          },
        );
      }
    });
  };

  useEffect(() => {
    setFilterForm({
      startDate: startDate ? new Date(`${startDate}Z`) : null,
      endDate: endDate ? new Date(`${endDate}Z`) : null,
      expiredContractOnly: expiredContractOnly === 'true',
      activationStatuses,
      keyword,
    });
  }, [startDate, endDate, expiredContractOnly, activationStatuses, keyword]);

  return (
    <div>
      <C.TitleBox>
        <div className="title">{`모뎀 관리`}</div>
      </C.TitleBox>
      <C.ListBox>
        <ModemFilterSection
          filterForm={filterForm}
          onChangeFilterForm={updateFilterForm}
          onSearch={() => applyFilters(filterForm)}
          onReset={resetFilters}
        />
        <M.Space h={28} />
        <M.Group justify="space-between" mb="sm">
          <M.Text fw={600}>모뎀 목록</M.Text>
          <ModemRegister onClickDelete={onClickDelete} checkBoxArr={checkBoxArr} />
        </M.Group>
        <ModemTable
          modemList={modemList?.list || []}
          checkBoxArr={checkBoxArr}
          isAllChecked={isAllSelected}
          onClickCheckBox={(e) => toggleItem(Number(e.target.id), e.target.checked)}
          onClickCheckBoxAll={(e) => toggleAll(e.target.checked)}
          totalPage={Math.ceil((modemList?.totalCount ?? 0) / Number(pgSize))}
        />
      </C.ListBox>
    </div>
  );
}
